import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { postAPI } from "../../utils/utils";
import { TEMPS_BUCKETS, eventComposition, firstDataDate } from "./tempsCollaData";

// Qui va venir a cada esdeveniment i quan va venir per primer cop a la colla.
export function useTempsColla(eventIds) {
    const [data, setData] = useState(null);
    const key = eventIds.join(',');

    useEffect(() => {
        if (eventIds.length === 0) return;
        let active = true;
        postAPI('/stats/temps_colla', { events: eventIds }, response => {
            if (!active) return;
            setData({
                firstAttendance: response?.first_attendance || {},
                attendees: response?.attendees || {},
            });
        });
        return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return data;
}

function TempsCollaLegend() {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: '#475569', fontWeight: 500 }}>
            {TEMPS_BUCKETS.map(bucket => (
                <span key={bucket.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: bucket.color }} />
                    {bucket.short}
                </span>
            ))}
        </div>
    );
}

function ModeSwitch({ value, onChange, options }) {
    return (
        <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 6, padding: 2 }}>
            {options.map(([key, label]) => (
                <button
                    key={key}
                    onClick={() => onChange(key)}
                    style={{
                        padding: '5px 12px',
                        fontSize: 13,
                        fontWeight: 500,
                        border: 'none',
                        borderRadius: 5,
                        cursor: 'pointer',
                        background: value === key ? '#fff' : 'transparent',
                        color: value === key ? '#1e293b' : '#64748b',
                        boxShadow: value === key ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                    }}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

// Una barra per esdeveniment, apilada pel temps a la colla de qui hi va venir.
export function TempsCollaPerEvent({ events, tempsColla, castellersInfo, filters, height = 320 }) {
    const [mode, setMode] = useState('persones');
    const compositions = useMemo(() => {
        if (!tempsColla) return [];
        return events.map(event => eventComposition(event, tempsColla, castellersInfo, filters));
    }, [events, tempsColla, castellersInfo, filters]);

    const labels = events.map(event => `${moment(event['data-esperada-inici']).format('DD/MM/YY')} ${event.title}`);

    const data = {
        labels,
        datasets: TEMPS_BUCKETS.map((bucket, i) => ({
            label: bucket.short,
            data: compositions.map(counts => {
                if (mode === 'persones') return counts[i];
                const total = counts.reduce((a, b) => a + b, 0);
                return total > 0 ? Math.round(1000 * counts[i] / total) / 10 : 0;
            }),
            backgroundColor: bucket.color,
            borderColor: '#fff',
            borderWidth: { top: 2 },
            borderSkipped: false,
        })),
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        plugins: {
            legend: { display: false },
            datalabels: { display: false },
            tooltip: {
                backgroundColor: '#1e293b',
                titleFont: { size: 12, weight: 500 },
                bodyFont: { size: 12 },
                padding: 12,
                cornerRadius: 6,
                boxPadding: 4,
                callbacks: {
                    title: items => labels[items[0].dataIndex],
                    label: ctx => ` ${ctx.dataset.label}: ${ctx.raw}${mode === 'percentatge' ? '%' : ''}`,
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
                border: { display: false },
                ticks: {
                    callback: index => labels[index]?.split(' ')?.[0] ?? '',
                    font: { size: 11 },
                    color: '#94a3b8',
                    maxRotation: 45,
                    minRotation: 45,
                },
            },
            y: {
                stacked: true,
                beginAtZero: true,
                max: mode === 'percentatge' ? 100 : undefined,
                grid: { color: '#f1f5f9' },
                border: { display: false },
                ticks: {
                    font: { size: 11 },
                    color: '#94a3b8',
                    padding: 8,
                    callback: value => mode === 'percentatge' ? `${value}%` : value,
                },
            },
        },
    };

    const since = firstDataDate(tempsColla?.firstAttendance || {});

    return (
        <>
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 16px 16px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '0 0 12px 12px' }}>
                    <TempsCollaLegend />
                    <ModeSwitch value={mode} onChange={setMode} options={[['persones', 'Persones'], ['percentatge', '%']]} />
                </div>
                <div style={{ width: '100%', height }}>
                    <Bar data={data} options={options} />
                </div>
            </div>
            {since ? (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
                    Temps des del primer esdeveniment al qual va venir cada persona. Hi ha dades des del {moment(since).format('D/M/YYYY')}: qui ja hi era abans compta des d'aleshores.
                </div>
            ) : null}
        </>
    );
}
